import { RouteParams } from '@components/router';

/*
 * Our student urls(lesson, meeting, people, etc) are all prefixed with
 * a slug of those entities. They are purely for the purpose of
 * making the url more readable, and we are ignoring them.
 * So this method checks if the parameter has a UUID and if it does
 * it sets the parameter value after stripping the slug.
 *
 * Exported for testing purposes.
 */
export function processRouteParams(source: RouteParams) {
  if (!source.courseId) {
    return source;
  }

  const UUID_LEN = 36; // The length of a v4 UUID
  const DELIM = '--';
  const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  const result = source;
  // The ID section of the slug normally starts with a --, which is disallowed
  // in the human-section. However, -- can appear in other IDs, such as our
  // payment IDs. As a fallback, if -- is missing (e.g. an auto-formatter
  // turned it into an em-dash), we treat the trailing UUID_LEN chars as the
  // ID if they look like a UUID.
  for (const k in result) {
    const val = result[k];
    // The value is not a slug in this case, since slugs are always some text
    // followed by `{separator}{UUID}`
    if (val.length <= UUID_LEN) {
      continue;
    }

    let slug: string;
    let id: string;

    const delimPos = val.indexOf(DELIM);
    if (delimPos >= 0) {
      slug = val.slice(0, delimPos);
      id = val.slice(delimPos + DELIM.length);
    } else {
      const candidate = val.slice(-UUID_LEN);
      if (!UUID_RE.test(candidate)) {
        continue;
      }
      id = candidate;
      // Strip any trailing dash/em-dash characters left over from the
      // mangled separator.
      slug = val.slice(0, -UUID_LEN).replace(/[-—]+$/, '');
    }

    // Save courseSlug as a separate param as it's used to build the URL
    // in a way that's consistent with the currently displayed one.
    if (k === 'courseId') {
      result.courseSlug = slug;
    }
    // Modify result[k] (e.g. result.lessonId, etc) to just hold the ID portion
    // of the vanity URL.
    result[k] = id;
  }

  return result;
}
